<template>
  <div
    class="ipr-chart"
    :class="{ 'is-static': isStatic, 'is-horizontal': isHorizontal }"
  >
    <ScatterChart
      v-bind="$attrs"
      :data-sets="dataSets"
      draw-x-axis
      draw-y-axis
      draw-curve
      mouse-tracking-ignore-x
      draw-tool-tips
      absolute-scale
      :tick-frequency-x="ddvTickFrequencies?.x"
      :tick-frequency-y="ddvTickFrequencies?.y"
    >
      <template
        v-if="dataSets.every(ds => ds.points.length === 0)"
        #background="{ domState, margins }"
      >
        <rect
          v-if="domState.plotWidth > 0 && domState.plotHeight > 0"
          class="plotBg"
          :x="margins.left"
          :y="margins.top"
          :width="domState.plotWidth"
          :height="domState.plotHeight"
        />
        <text
          x="50%"
          y="50%"
          fill="var(--plotColor0)"
          text-anchor="middle"
          dominant-baseline="middle"
          >No Data</text
        >
      </template>
      <template v-if="dataSets.every(ds => ds.points.length === 0)" #crosshairs>
        Return none
      </template>

      <template #tooltip="{ chartId, toolTipInfo }">
        <ToolTip
          :id="`ddv-tt-${chartId}`"
          :tool-tip-info="getFilteredToolTip(toolTipInfo)"
        />
      </template>
      <template
        #perSeries="{
          dataSeriesNumber,
          nearestPoint,
          screenPoints,
          xScaler,
          yScaler
        }"
      >
        <LinePlot
          :is-active="activeDataSets[dataSeriesNumber]"
          :screen-points="screenPoints"
          :data-series-number="dataSeriesNumber"
          curve-interpolator="catmull-rom"
        />
        <g
          v-if="activeDataSets[dataSeriesNumber]"
          class="operating-point"
          :data-series-number="dataSeriesNumber"
          :transform="`
            translate(
              ${xScaler.scale(
                dataSets[dataSeriesNumber].operatingPoint.point.x
              )},
              ${yScaler.scale(
                dataSets[dataSeriesNumber].operatingPoint.point.y
              )}
            )`"
        >
          <g>
            <circle
              :cx="0"
              :cy="0"
              :r="5"
              stroke="var(--activePlotColor)"
              :stroke-width="2"
              fill="transparent"
            />
            <circle
              :cx="0"
              :cy="0"
              :r="5"
              stroke="var(--activePlotColor)"
              :stroke-opacity="0.3"
              :stroke-width="8"
              fill="transparent"
            />
            <circle :cx="0" :cy="0" :r="4" fill="var(--plotSurface)" />
          </g>
        </g>
        <NearestPoint
          v-if="nearestPoint?.node && activeDataSets[dataSeriesNumber]"
          :data-series-number="dataSeriesNumber"
          :nearest-point="nearestPoint"
        />
      </template>
    </ScatterChart>
    <CustomLegend
      v-if="drawLegend"
      v-model="activeDataSets"
      :model="dataSets"
      :static="isStatic"
      :horizontal="isHorizontal"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref } from "vue";
import type { ToolTipInfo } from "@incytestudios/ddv/types/common";
import {
  ScatterChart,
  LinePlot,
  NearestPoint,
  ToolTip
} from "@incytestudios/ddv";
import { toolTipFilter } from "@/utils/charts/utils";
import CustomLegend from "./CustomLegend.vue";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import type { Scenario, WellProperties } from "@/composables/forms/formTypes";
import { useIPRChartData } from "@/libs/charting/ipr/useIPRChartData";
import {
  scenarioKey,
  scenarioCalculationsKey,
  sizingKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import { injectStrict } from "@/utils/utils";
import type { DataSetExtra } from "@/libs/charting/ipr/d";

const props = defineProps<{
  wellProperties?: WellProperties;
  scenario?: Scenario | null;
  scenarioCalculations?: ScenarioCalculations;
  drawLegend?: boolean;
  isStatic?: boolean;
  isHorizontal?: boolean;
}>();
const ddvTickFrequencies = inject<{ x: string; y: string }>(
  "ddvTickFrequencies"
);

const _scenario = props.scenario
  ? ref<Scenario>(props.scenario)
  : injectStrict(scenarioKey);
const _calcs =
  props.scenarioCalculations || injectStrict(scenarioCalculationsKey);
const _sizing = injectStrict(sizingKey, null);
const { getPressureUnit, getLiquidFlowUnit } = useScenarioUnits(
  _scenario,
  _sizing
);
const targetPressureUnit = getPressureUnit(
  "Producing Bottomhole Pressure at Datum [Pwf].write.PSIA"
);
const targetLiquidFlowUnit = getLiquidFlowUnit(
  "Target Liquid Rate.convert.STB/D"
);

const chartData = computed(() =>
  useIPRChartData(
    _calcs,
    targetPressureUnit.value,
    targetLiquidFlowUnit.value,
    props.wellProperties
  )
);

// Comment back in when calcs are ready
const dataSets = computed((): DataSetExtra[] =>
  chartData.value
    ? [
        chartData.value.straightDataSet,
        chartData.value.vogelDataSet,
        chartData.value.compositeDataSet,
        chartData.value.vogelModifiedDataSet
      ]
    : []
);

const activeDataSets = ref(dataSets.value.map(() => true));

function getFilteredToolTip(tti: ToolTipInfo): ToolTipInfo {
  return toolTipFilter(tti, activeDataSets.value);
}
</script>

<style lang="scss" scoped>
.ipr-chart {
  width: 95%;
  display: grid;
  grid-template-columns: 3fr max-content;

  &.is-horizontal {
    grid-template-columns: 1fr;
  }

  &.is-static {
    :deep(.ddv) {
      &.isPlotHovered {
        cursor: auto !important;
      }
    }
  }
}

@media screen and (max-width: 870px) {
  .ipr-chart {
    grid-template-columns: 1fr;
    width: 90%;
  }
}
</style>
